/* ============================================================
   Почта — корпоративная деловая переписка.
   Папки (Входящие/Отправленные/Черновики/Важные/Архив) +
   список писем + чтение + написать/ответить/переслать.
   ============================================================ */
import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Inbox, Send, FileEdit, Star, Archive, Plus, Reply, ReplyAll,
  Forward, Paperclip, X, FileText, Mail as MailIcon, ArrowLeft,
} from 'lucide-react';
import { mailApi, getErrorMessage } from '@/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { formatDateTime } from '@/lib/format';
import type {
  MailFolder, MailListItem, MailDetail, MailUser, MailAttachment,
} from '@/types';
import './mail.css';

const FOLDERS: { key: MailFolder; label: string; icon: typeof Inbox }[] = [
  { key: 'inbox', label: 'Входящие', icon: Inbox },
  { key: 'sent', label: 'Отправленные', icon: Send },
  { key: 'drafts', label: 'Черновики', icon: FileEdit },
  { key: 'important', label: 'Важные', icon: Star },
  { key: 'archive', label: 'Архив', icon: Archive },
];

type ComposeMode =
  | { kind: 'new' }
  | { kind: 'reply'; source: MailDetail; all: boolean }
  | { kind: 'forward'; source: MailDetail };

export function MailPage() {
  const { toast } = useToast();
  const [folder, setFolder] = useState<MailFolder>('inbox');
  const [list, setList] = useState<MailListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [active, setActive] = useState<MailDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compose, setCompose] = useState<ComposeMode | null>(null);

  const loadList = useCallback(async (f: MailFolder) => {
    setLoadingList(true);
    try {
      setList(await mailApi.list(f));
    } catch (e) {
      toast(getErrorMessage(e), 'error');
    } finally {
      setLoadingList(false);
    }
  }, [toast]);

  useEffect(() => { loadList(folder); setActiveId(null); setActive(null); }, [folder, loadList]);

  const openMail = async (id: number) => {
    setActiveId(id);
    setLoadingDetail(true);
    try {
      const m = await mailApi.get(id);
      setActive(m);
      setList((prev) => prev.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    } catch (e) {
      toast(getErrorMessage(e), 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  const onImportant = async () => {
    if (!active) return;
    await mailApi.toggleImportant(active.id);
    toast('Отметка обновлена', 'success');
    loadList(folder);
  };
  const onArchive = async () => {
    if (!active) return;
    const archived = await mailApi.toggleArchive(active.id);
    toast(archived ? 'В архиве' : 'Возвращено из архива', 'success');
    setActiveId(null); setActive(null);
    loadList(folder);
  };

  return (
    <AppLayout>
      <div className="mail">
        {/* Папки */}
        <div className="mail__folders">
          <Button variant="primary" style={{ width: '100%', marginBottom: 14 }}
            onClick={() => setCompose({ kind: 'new' })}>
            <Plus size={16} /> Написать
          </Button>
          {FOLDERS.map((f) => (
            <button key={f.key}
              className={`mail__folder${f.key === folder ? ' mail__folder--active' : ''}`}
              onClick={() => setFolder(f.key)}>
              <f.icon size={17} />
              <span>{f.label}</span>
            </button>
          ))}
        </div>

        {/* Список писем */}
        <div className={`mail__list${activeId ? ' mail__list--hidden-mobile' : ''}`}>
          <div className="mail__list-head">{FOLDERS.find((f) => f.key === folder)?.label}</div>
          {loadingList ? <Spinner label="Загрузка…" /> :
            list.length === 0 ? <EmptyState icon={<MailIcon size={28} />} title="Писем нет" /> : (
              <div className="mail__items">
                {list.map((m) => (
                  <button key={m.id}
                    className={`mail__item${m.id === activeId ? ' mail__item--active' : ''}${!m.is_read && folder !== 'sent' && folder !== 'drafts' ? ' mail__item--unread' : ''}`}
                    onClick={() => openMail(m.id)}>
                    <div className="mail__item-top">
                      <span className="mail__item-from">
                        {folder === 'sent' || folder === 'drafts'
                          ? `Кому: ${m.to_brief || '—'}`
                          : (m.sender?.short_name || '—')}
                      </span>
                      <span className="mail__item-time">{formatDateTime(m.sent_at || m.created_at)}</span>
                    </div>
                    <div className="mail__item-subject">
                      {m.importance === 'high' && <span className="mail__flag">!</span>}
                      {m.is_important_me && <Star size={12} className="mail__star" />}
                      {m.subject || '(без темы)'}
                      {m.has_attachments && <Paperclip size={12} className="mail__clip" />}
                    </div>
                    <div className="mail__item-preview">{m.preview}</div>
                  </button>
                ))}
              </div>
            )}
        </div>

        {/* Чтение письма */}
        <div className={`mail__reader${activeId ? '' : ' mail__reader--hidden-mobile'}`}>
          {!active ? (
            <div className="mail__placeholder"><MailIcon size={40} /><p>Выберите письмо</p></div>
          ) : loadingDetail ? <Spinner /> : (
            <>
              <div className="mail__reader-head">
                <button className="mail__back" onClick={() => { setActiveId(null); setActive(null); }}>
                  <ArrowLeft size={18} />
                </button>
                <h2>{active.subject || '(без темы)'}</h2>
                <div className="mail__reader-actions">
                  <button title="Ответить" onClick={() => setCompose({ kind: 'reply', source: active, all: false })}><Reply size={17} /></button>
                  <button title="Ответить всем" onClick={() => setCompose({ kind: 'reply', source: active, all: true })}><ReplyAll size={17} /></button>
                  <button title="Переслать" onClick={() => setCompose({ kind: 'forward', source: active })}><Forward size={17} /></button>
                  <button title="Важное" onClick={onImportant}><Star size={17} /></button>
                  <button title="В архив" onClick={onArchive}><Archive size={17} /></button>
                </div>
              </div>
              <div className="mail__meta">
                <div><span className="mail__meta-label">От:</span> {active.sender?.name || '—'}</div>
                <div><span className="mail__meta-label">Кому:</span> {active.recipients.filter((r) => r.kind === 'to').map((r) => r.user?.name || r.external_email).join(', ')}</div>
                {active.recipients.some((r) => r.kind === 'cc') && (
                  <div><span className="mail__meta-label">Копия:</span> {active.recipients.filter((r) => r.kind === 'cc').map((r) => r.user?.name || r.external_email).join(', ')}</div>
                )}
                <div className="mail__meta-time">{formatDateTime(active.sent_at || active.created_at)}</div>
                {(active.related_contract_number || active.related_object_address) && (
                  <div className="mail__context">
                    {active.related_contract_number && <span>Договор: {active.related_contract_number}</span>}
                    {active.related_object_address && <span>Объект: {active.related_object_address}</span>}
                  </div>
                )}
                {active.recipients.some((r) => r.is_executor && r.resolution) && (
                  <div className="mail__resolution">
                    <strong>Резолюция:</strong>{' '}
                    {active.recipients.find((r) => r.resolution)?.resolution}
                  </div>
                )}
              </div>
              <div className="mail__body">{active.body}</div>
              {active.attachments.length > 0 && (
                <div className="mail__attachments">
                  {active.attachments.map((a) => <MailAttachmentView key={a.id} att={a} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {compose && (
        <ComposeModal
          mode={compose}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); loadList(folder); toast('Письмо отправлено', 'success'); }}
          onError={(m) => toast(m, 'error')}
        />
      )}
    </AppLayout>
  );
}

function MailAttachmentView({ att }: { att: MailAttachment }) {
  if (att.is_image) {
    return (
      <a href={att.url} target="_blank" rel="noreferrer" className="mail__att-image">
        <img src={att.url} alt={att.filename} />
      </a>
    );
  }
  const kb = att.size ? Math.max(1, Math.round(att.size / 1024)) : 0;
  return (
    <a href={att.url} target="_blank" rel="noreferrer" className="mail__att-file">
      <FileText size={18} />
      <span><span className="mail__att-name">{att.filename}</span>{kb > 0 && <span className="mail__att-size">{kb} КБ</span>}</span>
    </a>
  );
}

function ComposeModal({ mode, onClose, onSent, onError }: {
  mode: ComposeMode;
  onClose: () => void;
  onSent: () => void;
  onError: (m: string) => void;
}) {
  const isReply = mode.kind === 'reply';
  const isForward = mode.kind === 'forward';
  const title = isReply ? (mode.all ? 'Ответить всем' : 'Ответить') : isForward ? 'Переслать' : 'Новое письмо';

  const { data: contacts } = useQuery({ queryKey: ['mail-contacts'], queryFn: () => mailApi.contacts() });
  const [toIds, setToIds] = useState<number[]>([]);
  const [subject, setSubject] = useState(
    isReply ? '' : isForward ? '' : ''
  );
  const [body, setBody] = useState('');
  const [resolution, setResolution] = useState('');
  const [important, setImportant] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const toggleTo = (id: number) =>
    setToIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    setSending(true);
    try {
      if (isReply) {
        await mailApi.reply(mode.source.id, body, mode.all, files);
      } else if (isForward) {
        if (toIds.length === 0) { onError('Выберите получателя'); setSending(false); return; }
        await mailApi.forward(mode.source.id, { to_ids: toIds, note: body, resolution }, files);
      } else {
        if (toIds.length === 0) { onError('Выберите получателя'); setSending(false); return; }
        await mailApi.compose({
          subject, body, to_ids: toIds,
          importance: important ? 'high' : 'normal', is_draft: false,
        }, files);
      }
      onSent();
    } catch (e) {
      onError(getErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const saveDraft = async () => {
    setSending(true);
    try {
      await mailApi.compose({ subject, body, to_ids: toIds, is_draft: true }, files);
      onSent();
    } catch (e) {
      onError(getErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={title} width={640}
      footer={<>
        {!isReply && <Button onClick={saveDraft} disabled={sending}>В черновики</Button>}
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={sending} onClick={submit}>
          {sending ? 'Отправка…' : 'Отправить'}
        </Button>
      </>}>
      {!isReply && (
        <div className="field">
          <label className="field__label">Кому</label>
          <div className="mail__recipients">
            {contacts?.map((c: MailUser) => (
              <button key={c.id} type="button"
                className={`mail__chip${toIds.includes(c.id) ? ' mail__chip--on' : ''}`}
                onClick={() => toggleTo(c.id)}>
                {c.short_name}
              </button>
            ))}
          </div>
        </div>
      )}
      {mode.kind === 'new' && (
        <div className="field">
          <label className="field__label">Тема</label>
          <input className="field__input" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
      )}
      {isForward && (
        <div className="field">
          <label className="field__label">Резолюция исполнителю (необязательно)</label>
          <input className="field__input" value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            placeholder="Напр.: в работу до 18.06" />
        </div>
      )}
      <div className="field">
        <label className="field__label">{isReply || isForward ? 'Комментарий' : 'Текст'}</label>
        <textarea className="field__textarea" rows={6} value={body}
          onChange={(e) => setBody(e.target.value)} />
      </div>
      <div className="mail__compose-foot">
        <label className="mail__attach-label">
          <Paperclip size={15} /> Прикрепить
          <input type="file" multiple style={{ display: 'none' }}
            onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files ?? [])])} />
        </label>
        {mode.kind === 'new' && (
          <label className="mail__important-toggle">
            <input type="checkbox" checked={important} onChange={(e) => setImportant(e.target.checked)} /> Важное
          </label>
        )}
      </div>
      {files.length > 0 && (
        <div className="mail__pending">
          {files.map((f, i) => (
            <span key={i} className="mail__pending-item">
              <FileText size={13} /> {f.name}
              <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </Modal>
  );
}
